import React, { useState, useRef, useEffect } from "react";
import { useEntity } from "../../../hooks/use-entity.hook";
import { Flaggers } from "../../../common/store/flaggers/flaggers.enum";
import { FlaggersMethods } from "../../../common/store/flaggers/flaggers.methods";
import ArrowDownIcon from "../../icons/arrow-down.icon";
import cn from "../../../Utils/class-names.utils";
import { useTableSort, SortConfig } from "../../../hooks/use-table-sort.hook";
import styles from "./name-color-table.module.scss";

export interface NameColorData {
  id: number;
  name: string;
  associatedMSPs: number;
  color: string;
  isDisabled?: boolean;
}

type SortField = keyof NameColorData;

interface SortableHeaderCellProps {
  label: string;
  field: SortField;
  sortConfig: SortConfig<NameColorData>;
  onSort: (field: SortField) => void;
  className?: string;
}

const SortableHeaderCell = ({
  label,
  field,
  sortConfig,
  onSort,
  className,
}: SortableHeaderCellProps) => {
  const isActive = sortConfig.field === field && sortConfig.direction !== null;
  const direction = isActive ? sortConfig.direction : null;

  return (
    <div 
      className={cn(styles.headerCell, className, "pointer")}
      onClick={() => onSort(field)}
    >
      <div className={styles.headerCellContent}>
        <div className={styles.headerText}>{label}</div>
      </div>

      {isActive && (
        <div className={styles.headerCellWithArrow}>
          <ArrowDownIcon
            className={cn({
              rotate180: direction === "desc",
            })}
            style={{
              transition: "transform 0.2s ease",
            }}
          />
        </div>
      )}
    </div>
  );
};

interface NameColorTableProps<T extends NameColorData> {
  data: T[];
  onDataChange: (data: T[]) => void;
  firstColumnLabel: string;
  disableModalFlagger?: Flaggers;
  deleteModalFlagger?: Flaggers;
  renderRow: (props: {
    item: T;
    editingId: number | null;
    editingValue: string;
    inputRef: React.RefObject<HTMLInputElement>;
    onNameClick: (item: T) => void;
    onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onInputBlur: () => void;
    onColorSelect: (id: number, color: string) => void;
    onToggleDisable?: (id: number) => void;
    onDelete?: (id: number) => void;
  }) => React.ReactNode;
  renderDisableModal?: (props: {
    show: boolean;
    associatedMSPs: number;
    onHide: () => void;
    onConfirm: () => void;
    isLoading?: boolean;
  }) => React.ReactNode;
  renderDeleteModal?: (props: {
    show: boolean;
    associatedMSPs: number;
    onHide: () => void;
    onConfirm: () => void;
    isLoading?: boolean;
  }) => React.ReactNode;
}

function NameColorTable<T extends NameColorData>({
  data,
  onDataChange,
  firstColumnLabel,
  disableModalFlagger,
  deleteModalFlagger,
  renderRow,
  renderDisableModal,
  renderDeleteModal,
}: NameColorTableProps<T>) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { sortedData, sortConfig, handleSort } = useTableSort<T>(data, {
    initialField: "name" as keyof T,
    initialDirection: "asc",
  });

  const {
    entityData: disableModalData,
    setEntityData: setDisableModalFlagger,
  } = useEntity<{ show?: boolean; isLoading?: boolean; tagId?: number; associatedMSPs?: number } | null>({
    entityType: disableModalFlagger || Flaggers.DisableToggleTagModalFlagger,
  });

  const {
    entityData: deleteModalData,
    setEntityData: setDeleteModalFlagger,
  } = useEntity<{ show?: boolean; isLoading?: boolean; tagId?: number; associatedMSPs?: number } | null>({
    entityType: deleteModalFlagger || Flaggers.DeleteTagModalFlagger,
  });

  useEffect(() => {
    if (editingId !== null && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingId]);

  const handleNameClick = (item: T) => {
    if (item.isDisabled) return;
    setEditingId(item.id);
    setEditingValue(item.name);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingValue(e.target.value);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSaveEdit();
    }
    if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const handleInputBlur = () => {
    handleSaveEdit();
  };

  const handleSaveEdit = () => {
    if (editingId === null) return;

    const updatedData = data.map((item) =>
      item.id === editingId
        ? { ...item, name: editingValue.trim() || item.name }
        : item
    );
    onDataChange(updatedData);
    setEditingId(null);
    setEditingValue("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const handleColorSelect = (id: number, color: string) => {
    const updatedData = data.map((item) =>
      item.id === id ? { ...item, color } : item
    );
    onDataChange(updatedData);
  };

  const handleToggleDisable = (id: number) => {
    const item = data.find((t) => t.id === id);
    if (!item) return;

    if (item.isDisabled) {
      const updatedData = data.map((t) =>
        t.id === id ? { ...t, isDisabled: false } : t
      );
      onDataChange(updatedData);
    } else {
      setDisableModalFlagger(
        { show: true, tagId: item.id, associatedMSPs: item.associatedMSPs },
        FlaggersMethods.UpdateFlagger
      );
    }
  };

  const handleConfirmDisable = () => {
    if (disableModalData?.tagId) {
      const updatedData = data.map((item) =>
        item.id === disableModalData.tagId ? { ...item, isDisabled: true } : item
      );
      onDataChange(updatedData);
      setDisableModalFlagger(null, FlaggersMethods.UpdateFlagger);
    }
  };

  const handleCloseDisableModal = () => {
    setDisableModalFlagger(null, FlaggersMethods.UpdateFlagger);
  };

  const handleDelete = (id: number) => {
    const item = data.find((t) => t.id === id);
    if (!item) return;
    setDeleteModalFlagger(
      { show: true, tagId: item.id, associatedMSPs: item.associatedMSPs },
      FlaggersMethods.UpdateFlagger
    );
  };

  const handleConfirmDelete = () => {
    if (deleteModalData?.tagId) {
      const updatedData = data.filter((item) => item.id !== deleteModalData.tagId);
      onDataChange(updatedData);
      setDeleteModalFlagger(null, FlaggersMethods.UpdateFlagger);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalFlagger(null, FlaggersMethods.UpdateFlagger);
  };

  return (
    <div className={cn(styles.table, "table-wrapper overflow-x-auto hide-scrollbar")}>
      <div className={styles.header}>
        <div className={styles.headerColumn}>
          <SortableHeaderCell
            label={firstColumnLabel}
            field="name"
            sortConfig={sortConfig as SortConfig<NameColorData>}
            onSort={handleSort as (field: SortField) => void}
          />
        </div>

        <div className={styles.headerColumnFixed}>
          <SortableHeaderCell
            label="Associated MSPs"
            field="associatedMSPs"
            sortConfig={sortConfig as SortConfig<NameColorData>}
            onSort={handleSort as (field: SortField) => void}
          />
        </div>

        <div className={styles.headerColumnFixed}>
          <SortableHeaderCell
            label="Color"
            field="color"
            sortConfig={sortConfig as SortConfig<NameColorData>}
            onSort={handleSort as (field: SortField) => void}
          />
        </div>

        <div className={styles.headerColumn} />
      </div>

      {sortedData.map((item) =>
        renderRow({
          item,
          editingId,
          editingValue,
          inputRef: inputRef as React.RefObject<HTMLInputElement>,
          onNameClick: handleNameClick,
          onInputChange: handleInputChange,
          onInputKeyDown: handleInputKeyDown,
          onInputBlur: handleInputBlur,
          onColorSelect: handleColorSelect,
          onToggleDisable: handleToggleDisable,
          onDelete: handleDelete,
        })
      )}

      {renderDisableModal && (
        <>
          {renderDisableModal({
            show: disableModalData?.show || false,
            associatedMSPs: disableModalData?.associatedMSPs || 0,
            onHide: handleCloseDisableModal,
            onConfirm: handleConfirmDisable,
            isLoading: disableModalData?.isLoading || false,
          })}
        </>
      )}

      {renderDeleteModal && (
        <>
          {renderDeleteModal({
            show: deleteModalData?.show || false,
            associatedMSPs: deleteModalData?.associatedMSPs || 0,
            onHide: handleCloseDeleteModal,
            onConfirm: handleConfirmDelete,
            isLoading: deleteModalData?.isLoading || false,
          })}
        </>
      )}
    </div>
  );
}

export default NameColorTable;

